<template>
  <div class="card p-4 flex flex-col gap-3" data-testid="student-imports-list">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">Recent Imports</h3>
        <p class="text-xs text-gray-500">Background student imports from the last 7 days.</p>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="inFlightCount > 0"
          class="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary-700 font-medium">
          <i class="pi pi-spin pi-spinner mr-1"></i>{{ inFlightCount }} in progress
        </span>
        <Button
          icon="pi pi-refresh"
          text
          rounded
          size="small"
          :loading="loading"
          class="!p-1 text-gray-500 hover:text-gray-700"
          @click="refetch"
        />
      </div>
    </div>

    <div v-if="loading && items.length === 0" class="p-6 text-center text-gray-400">
      <i class="pi pi-spin pi-spinner text-xl"></i>
    </div>

    <div v-else-if="items.length === 0" class="p-6 text-center text-gray-400 flex flex-col items-center gap-2">
      <i class="pi pi-inbox text-2xl opacity-40"></i>
      <span class="text-sm">No background imports yet.</span>
    </div>

    <ul v-else class="flex flex-col divide-y border rounded-lg overflow-hidden">
      <li
        v-for="item in items"
        :key="item.id"
        class="p-3 bg-white hover:bg-gray-50 transition-colors flex flex-col gap-1.5"
        :data-testid="`student-import-item-${item.id}`">
        <div class="flex items-start justify-between gap-3">
          <div class="mt-0.5 shrink-0">
            <i v-if="item.status === 'completed'" class="pi pi-check-circle text-green-600 text-sm"></i>
            <i v-else-if="item.status === 'completed_with_errors'" class="pi pi-exclamation-triangle text-orange-500 text-sm"></i>
            <i v-else-if="item.status === 'failed'" class="pi pi-times-circle text-red-500 text-sm"></i>
            <i v-else class="pi pi-spin pi-spinner text-primary text-sm"></i>
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-800">
              {{ statusLabel(item) }}
            </p>
            <div class="text-[11px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-2">
              <span :class="statusBadgeClass(item.status)" class="px-1.5 py-0.5 rounded font-medium">
                {{ formatStatus(item.status) }}
              </span>
              <span>{{ item.successful_rows.toLocaleString() }} / {{ item.total_rows.toLocaleString() }} students</span>
              <span v-if="item.failed_rows > 0" class="text-orange-600">
                {{ item.failed_rows.toLocaleString() }} error{{ item.failed_rows === 1 ? '' : 's' }}
              </span>
              <span class="ml-auto text-gray-400">{{ formatDate(item.created_at) }}</span>
            </div>
          </div>

          <Button
            v-if="canViewDetails(item)"
            label="View"
            icon="pi pi-search"
            size="small"
            text
            class="!p-1"
            @click="openDetails(item.id)"
          />
        </div>

        <div
          v-if="item.status === 'processing' || item.status === 'pending'"
          class="w-full bg-gray-200 rounded-full h-1.5">
          <div
            class="h-1.5 rounded-full transition-all duration-300"
            :class="item.progress > 0 ? 'bg-primary' : 'bg-primary sil-progress-indeterminate'"
            :style="item.progress > 0 ? { width: `${item.progress}%` } : { width: '40%' }"
          ></div>
        </div>

        <p
          v-if="item.status === 'failed' && item.error_message"
          class="text-[11px] text-red-500 italic mt-0.5">
          {{ item.error_message }}
        </p>
      </li>
    </ul>

    <StudentImportErrorsDialog
      v-model:visible="detailsVisible"
      :studentImportId="detailsId"
    />
  </div>
</template>

<script setup lang="ts">
import { useStudentImportsList, type StudentImportListItem } from '@/composables/queries/useStudentImport';
import StudentImportErrorsDialog from './StudentImportErrorsDialog.vue';

const { items, inFlightCount, loading, fetch, refetch, stopPolling } = useStudentImportsList({ first: 20 });

const detailsVisible = ref(false);
const detailsId = ref<string | null>(null);

function canViewDetails(item: StudentImportListItem): boolean {
  return item.failed_rows > 0 || item.status === 'failed' || item.status === 'completed_with_errors';
}

function openDetails(id: string) {
  detailsId.value = id;
  detailsVisible.value = true;
}

onMounted(() => {
  fetch();
});

onBeforeUnmount(() => stopPolling());

function statusLabel(item: { status: string; total_rows: number }): string {
  switch (item.status) {
  case 'pending': return `Queued (${item.total_rows.toLocaleString()} students)`;
  case 'processing': return `Importing ${item.total_rows.toLocaleString()} students…`;
  case 'completed': return `Imported ${item.total_rows.toLocaleString()} students`;
  case 'completed_with_errors': return 'Imported with errors';
  case 'failed': return 'Import failed';
  default: return item.status;
  }
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ');
}

function statusBadgeClass(status: string): string {
  switch (status) {
  case 'completed': return 'bg-green-100 text-green-700';
  case 'completed_with_errors': return 'bg-orange-100 text-orange-700';
  case 'failed': return 'bg-red-100 text-red-700';
  case 'processing': return 'bg-primary/10 text-primary-700';
  default: return 'bg-gray-100 text-gray-700';
  }
}

function formatDate(value: string): string {
  if (!value) return '';
  try {
    const d = new Date(value);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString();
  } catch {
    return value;
  }
}
</script>

<style scoped>
.sil-progress-indeterminate {
  animation: sil-shift 1.4s ease-in-out infinite;
}
@keyframes sil-shift {
  0% { transform: translateX(-30%); }
  50% { transform: translateX(80%); }
  100% { transform: translateX(-30%); }
}
</style>
